import { getPermissionValueAtPath } from "../../../components/PermissionGate/PermissionGate.tsx"
import { type ProductTourStep } from "../tour/product-tour.types.ts"
import { PRODUCT_TOUR } from "./product-tour-steps.ts"

/**
 * `requiredPage` is either a bare page code ("leads", "projects" — checked
 * against `permissions.pages`, same as the rest of the Guide) or a
 * `category.section.key` dot-path ("projects.milestones.project_milestone_view")
 * resolved via `getPermissionValueAtPath` — the same traversal `PermissionGate`
 * itself uses against `permissions.codes`, so a step's gate is never a
 * second, invented permission model.
 */
export const isProductTourStepPermitted = (
  requiredPage: string | undefined,
  pages: readonly string[] | null | undefined,
  codes: unknown
): boolean => {
  if (!requiredPage) return true
  if (!requiredPage.includes(".")) return (pages ?? []).includes(requiredPage)

  return getPermissionValueAtPath(codes, requiredPage)
}

/** A step with no `workspaceScope` belongs in either workspace (Home,
 * Dashboard, intro/complete). One with it declared only ever belongs in
 * the one it names — Company HQ has no Leads/Projects navigation at all,
 * so nothing built around them can ever apply there. */
export const isProductTourStepInWorkspace = (
  workspaceScope: ProductTourStep["workspaceScope"],
  currentWorkspace: "hq" | "branch"
): boolean => !workspaceScope || workspaceScope === currentWorkspace

/** Filtered once, when the tour starts — a step the user has no permission
 * for, or that doesn't belong in the current workspace, is simply never
 * part of their tour, not something skipped mid-run. */
export const visibleProductTourSteps = (
  pages: readonly string[] | null | undefined,
  codes: unknown,
  currentWorkspace: "hq" | "branch" = "branch"
): ProductTourStep[] =>
  PRODUCT_TOUR.steps.filter(
    (step) =>
      isProductTourStepPermitted(step.requiredPage, pages, codes) &&
      isProductTourStepInWorkspace(step.workspaceScope, currentWorkspace)
  )
